import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { formatNumber } from '../utils/currencyFormatter';
import { ArrowLeft, BarChart3, RefreshCw, X, Eye, Loader2, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TutorialVideoButton from '../components/TutorialVideoButton';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Cell, ReferenceLine } from 'recharts';

// Estas funciones se redefinen dentro del componente para usar el site del usuario
// Por ahora mantienen compatibilidad con el código existente
const fmt = (v, site = 'MCO') => formatNumber(v, site);
const fmtMoney = (v, site = 'MCO') => '$' + fmt(v, site);

const ChangeTag = ({ value }) => {
  if (!value || value === 0) return null;
  return <span className={value > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>{value > 0 ? '+' : ''}{value.toFixed(1)}%</span>;
};

// Modal de Diagnóstico de Publicación (con IA mejorada)
const ProductDiagnosticModal = ({ product, onClose }) => {
  const [diagnosis, setDiagnosis] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const diagnosisLoadedRef = useRef(false);

  const fetchDiagnosis = async () => {
    setLoadingAI(true);
    try {
      const response = await apiClient.post('/api/analytics/product-recommendation', {
        item_id: product.item_id,
        title: product.title,
        change: product.change,
        revenue_previous: product.revenue_previous,
        visits_pct: product.visits_pct,
        conversion_pct: product.conversion_pct,
        ticket_pct: product.ticket_pct,
        cause_type: product.cause_type,
        impact_visits: product.impact_visits,
        impact_conversion: product.impact_conversion,
        impact_ticket: product.impact_ticket,
        has_variations: product.has_variations || false
      });
      setDiagnosis(response.data);
    } catch (error) {
      toast.error('Error obteniendo diagnóstico');
    } finally {
      setLoadingAI(false);
    }
  };

  useEffect(() => {
    if (diagnosisLoadedRef.current) return;
    diagnosisLoadedRef.current = true;
    fetchDiagnosis();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-popover border border-border rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-popover border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {product.thumbnail && <img src={product.thumbnail} alt="" className="w-12 h-12 rounded-lg" />}
            <div>
              <h2 className="text-lg font-semibold text-foreground">Diagnóstico</h2>
              <div className="flex items-center gap-2">
                <p className="text-xs text-muted-foreground truncate max-w-[250px]">{product.title}</p>
                {product.has_variations && (
                  <span className="text-[10px] bg-cyan-600/30 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 rounded flex-shrink-0">Variantes</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Diagnóstico IA Principal */}
          {loadingAI ? (
            <div className="bg-muted border border-border rounded-xl p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600 dark:text-cyan-400 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">Analizando con IA...</p>
            </div>
          ) : diagnosis ? (
            <div className={`p-5 rounded-xl ${
              product.change >= 0 ? 'bg-green-900/30 border border-green-500/30' : 
              'bg-orange-900/30 border border-orange-500/30'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-bold text-foreground">{diagnosis.titulo}</h3>
                <span className={`text-xl font-bold ${product.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {product.change >= 0 ? '+' : ''}{fmtMoney(product.change)}
                </span>
              </div>
              <p className="text-muted-foreground text-sm mb-2">{diagnosis.analisis}</p>
              {diagnosis.stock_info && (
                <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded">Stock: {diagnosis.stock_info}</span>
              )}
            </div>
          ) : (
            <div className={`p-4 rounded-xl ${product.change >= 0 ? 'bg-green-900/20 border border-green-500/30' : 'bg-red-900/20 border border-red-500/30'}`}>
              <p className="text-lg font-bold mb-1">{product.cause_tag}</p>
              <p className={`text-2xl font-bold ${product.change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {product.change >= 0 ? '+' : ''}{fmtMoney(product.change)}
              </p>
            </div>
          )}
          
          {/* Plan de Acción */}
          {diagnosis && diagnosis.acciones && diagnosis.acciones.length > 0 && (
            <div className="bg-muted border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-muted-foreground mb-3">Plan de Acción</h3>
              <ul className="space-y-2">
                {diagnosis.acciones.map((accion, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      idx === 0 ? 'bg-red-500 text-white' :
                      idx === 1 ? 'bg-yellow-500 text-black' :
                      'bg-blue-500 text-white'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-foreground">{accion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Tabla Comparativa Completa */}
          <div className="bg-muted rounded-xl p-4">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Comparativa (Antes vs Ahora)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground text-xs">
                  <th className="text-left py-2">Métrica</th>
                  <th className="text-center py-2">Anterior</th>
                  <th className="text-center py-2">Actual</th>
                  <th className="text-center py-2">Variación</th>
                </tr>
              </thead>
              <tbody className="text-foreground">
                <tr className="border-t border-border">
                  <td className="py-2">Ingresos</td>
                  <td className="text-center text-muted-foreground">{fmtMoney(product.revenue_previous)}</td>
                  <td className="text-center font-medium">{fmtMoney(product.revenue_current)}</td>
                  <td className="text-center"><ChangeTag value={product.revenue_previous > 0 ? ((product.revenue_current - product.revenue_previous) / product.revenue_previous * 100) : 0} /></td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-2">Visitas</td>
                  <td className="text-center text-muted-foreground">{fmt(product.visits_previous)}</td>
                  <td className="text-center font-medium">{fmt(product.visits_current)}</td>
                  <td className="text-center"><ChangeTag value={product.visits_pct} /></td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-2">Conversión</td>
                  <td className="text-center text-muted-foreground">{product.conversion_previous?.toFixed(2)}%</td>
                  <td className="text-center font-medium">{product.conversion_current?.toFixed(2)}%</td>
                  <td className="text-center"><ChangeTag value={product.conversion_pct} /></td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-2">Órdenes</td>
                  <td className="text-center text-muted-foreground">{product.orders_previous}</td>
                  <td className="text-center font-medium">{product.orders_current}</td>
                  <td className="text-center"><ChangeTag value={product.orders_previous > 0 ? ((product.orders_current - product.orders_previous) / product.orders_previous * 100) : 0} /></td>
                </tr>
                <tr className="border-t border-border">
                  <td className="py-2">Ticket Promedio</td>
                  <td className="text-center text-muted-foreground">{fmtMoney(product.ticket_previous)}</td>
                  <td className="text-center font-medium">{fmtMoney(product.ticket_current)}</td>
                  <td className="text-center"><ChangeTag value={product.ticket_pct} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Impacto por Variable */}
          <div className="bg-muted rounded-xl p-4">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">Impacto monetario por variable</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-card/60 rounded-lg p-3 text-center">
                <p className="text-[10px] text-muted-foreground">Visitas</p>
                <p className={`text-lg font-bold ${product.impact_visits >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {product.impact_visits >= 0 ? '+' : ''}{fmtMoney(product.impact_visits)}
                </p>
                {diagnosis?.aportacion_cuadro && (
                  <p className="text-[10px] text-cyan-600 dark:text-cyan-400 mt-1">
                    {diagnosis.aportacion_cuadro.visitas} del cambio
                  </p>
                )}
              </div>
              <div className="bg-card/60 rounded-lg p-3 text-center">
                <p className="text-[10px] text-muted-foreground">Conversión</p>
                <p className={`text-lg font-bold ${product.impact_conversion >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {product.impact_conversion >= 0 ? '+' : ''}{fmtMoney(product.impact_conversion)}
                </p>
                {diagnosis?.aportacion_cuadro && (
                  <p className="text-[10px] text-cyan-600 dark:text-cyan-400 mt-1">
                    {diagnosis.aportacion_cuadro.conversion} del cambio
                  </p>
                )}
              </div>
              <div className="bg-card/60 rounded-lg p-3 text-center">
                <p className="text-[10px] text-muted-foreground">Ticket</p>
                <p className={`text-lg font-bold ${product.impact_ticket >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {product.impact_ticket >= 0 ? '+' : ''}{fmtMoney(product.impact_ticket)}
                </p>
                {diagnosis?.aportacion_cuadro && (
                  <p className="text-[10px] text-cyan-600 dark:text-cyan-400 mt-1">
                    {diagnosis.aportacion_cuadro.ticket_promedio} del cambio
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de Gráfico de Cascada (Waterfall Chart)
const WaterfallChart = ({ data, attribution }) => {
  if (!data || data.length === 0) return null;
  
  // Preparar datos para el gráfico de cascada
  // Necesitamos calcular las barras flotantes
  const chartData = [];
  let runningTotal = 0;
  
  data.forEach((item, index) => {
    if (item.type === 'start') {
      chartData.push({
        name: item.name,
        value: item.value,
        start: 0,
        end: item.value,
        fill: '#6366f1', // indigo para inicio
        isStart: true
      });
      runningTotal = item.value;
    } else if (item.type === 'end') {
      chartData.push({
        name: item.name,
        value: item.value,
        start: 0,
        end: item.value,
        fill: '#6366f1', // indigo para fin
        isEnd: true
      });
    } else if (item.type === 'delta') {
      const start = runningTotal;
      const end = runningTotal + item.value;
      chartData.push({
        name: item.name,
        displayValue: item.value,
        value: Math.abs(item.value),
        start: Math.min(start, end),
        end: Math.max(start, end),
        fill: item.value >= 0 ? '#22c55e' : '#ef4444', // verde/rojo
        isPositive: item.value >= 0,
        isDelta: true
      });
      runningTotal = end;
    }
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm text-foreground font-medium">{data.name}</p>
          <p className={`text-lg font-bold ${data.isDelta ? (data.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400') : 'text-blue-600 dark:text-blue-400'}`}>
            {data.isDelta ? (data.isPositive ? '+' : '-') : ''}{fmtMoney(data.isDelta ? data.displayValue : data.value)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card/60 border border-border rounded-xl p-4 mb-6">
      <h3 className="text-sm font-semibold text-muted-foreground mb-4">Afectación de ingresos</h3>
      
      {/* Resumen de atribución */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Cambio Total</p>
          <p className={`text-lg font-bold ${attribution?.delta_total >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {attribution?.delta_total >= 0 ? '+' : ''}{fmtMoney(attribution?.delta_total)}
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Por Visitas</p>
          <p className={`text-lg font-bold ${attribution?.impact_visits >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {attribution?.impact_visits >= 0 ? '+' : ''}{fmtMoney(attribution?.impact_visits)}
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Por Conversión</p>
          <p className={`text-lg font-bold ${attribution?.impact_conversion >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {attribution?.impact_conversion >= 0 ? '+' : ''}{fmtMoney(attribution?.impact_conversion)}
          </p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-[10px] text-muted-foreground">Por Ticket</p>
          <p className={`text-lg font-bold ${attribution?.impact_ticket >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {attribution?.impact_ticket >= 0 ? '+' : ''}{fmtMoney(attribution?.impact_ticket)}
          </p>
        </div>
      </div>
      
      {/* Gráfico visual */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barCategoryGap="20%">
            {/* Mid-gray (#64748b, slate-500) chosen for grid/axis so it reads on both
                the near-black dark theme and the white light theme background. */}
            <CartesianGrid strokeDasharray="3 3" stroke="#64748b" strokeOpacity={0.3} vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={{ stroke: '#64748b' }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 10 }}
              tickFormatter={(v) => '$' + (v / 1000000).toFixed(1) + 'M'}
              axisLine={{ stroke: '#64748b' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#64748b" />
            <Bar dataKey="end" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <p className="text-[10px] text-muted-foreground/70 text-center mt-2">
        Fórmula: Ingreso = Visitas × Conversión × Ticket Promedio (Método de Sustitución Secuencial)
      </p>
    </div>
  );
};

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const months = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
};

const Analytics = () => {
  const { user, loading, authToken } = useAuth();
  const { fetchWithCache, getStaleCache } = useDataCache();
  const navigate = useNavigate();
  const [causality, setCausality] = useState(null);
  const [publications, setPublications] = useState(null);
  const [loadingCausality, setLoadingCausality] = useState(false);
  const [loadingPubs, setLoadingPubs] = useState(false);
  const [period, setPeriod] = useState(7);

  // Custom date range
  const today = new Date().toISOString().split('T')[0];
  const [customMode, setCustomMode] = useState(false);
  const [customDateFrom, setCustomDateFrom] = useState(
    () => new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
  );
  const [customDateTo, setCustomDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(null);
  const [appliedDateTo, setAppliedDateTo] = useState(null);

  const displayDateRange = useMemo(() => {
    if (customMode && appliedDateFrom && appliedDateTo) {
      return `${formatDisplayDate(appliedDateFrom)} — ${formatDisplayDate(appliedDateTo)}`;
    }
    if (customMode) return null;
    const fromDate = new Date(Date.now() - period * 86400000).toISOString().split('T')[0];
    return `${formatDisplayDate(fromDate)} — ${formatDisplayDate(today)}`;
  }, [customMode, appliedDateFrom, appliedDateTo, period, today]);
  const [tab, setTab] = useState('causality');
  const [sortField, setSortField] = useState('revenue_30d');
  const [sortDir, setSortDir] = useState(-1);
  const [pubSearch, setPubSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);

  // Returns the currently active [dateFrom, dateTo] based on page state
  const getActiveDates = () => {
    if (customMode && appliedDateFrom && appliedDateTo)
      return [appliedDateFrom, appliedDateTo];
    const from = new Date(Date.now() - period * 86400000).toISOString().split('T')[0];
    return [from, today];
  };

  const fetchCausality = async (dateFrom, dateTo) => {
    const cacheKey = `analytics_causality_${dateFrom}_${dateTo}`;

    const cached = getStaleCache(cacheKey);
    if (cached && !cached.isStale) {
      setCausality(cached.data);
      return;
    }
    if (cached) {
      setCausality(cached.data);
    } else {
      setLoadingCausality(true);
    }

    try {
      const data = await fetchWithCache(
        cacheKey,
        async () => {
          const r = await apiClient.get(`/api/analytics/causality?date_from=${dateFrom}&date_to=${dateTo}`);
          return r.data;
        }
      );
      setCausality(data);
    } catch (e) {
      toast.error('Error');
      if (cached) setCausality(cached.data);
    } finally {
      setLoadingCausality(false);
    }
  };

  const fetchPublications = async (dateFrom, dateTo) => {
    const cacheKey = `analytics_publications_${dateFrom}_${dateTo}`;

    const cached = getStaleCache(cacheKey);
    if (cached && !cached.isStale) {
      setPublications(cached.data);
      return;
    }
    if (cached) {
      setPublications(cached.data);
    } else {
      setLoadingPubs(true);
    }

    try {
      const data = await fetchWithCache(
        cacheKey,
        async () => {
          const r = await apiClient.get(`/api/analytics/publications?date_from=${dateFrom}&date_to=${dateTo}`);
          return r.data;
        }
      );
      setPublications(data);
    } catch (e) {
      toast.error('Error');
      if (cached) setPublications(cached.data);
    } finally {
      setLoadingPubs(false);
    }
  };

  // Cargar datos automáticamente cuando el usuario entra a la página
  useEffect(() => {
    const [df, dt] = getActiveDates();
    fetchCausality(df, dt);
    fetchPublications(df, dt);
    fetchSyncMeta();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch cuando cambia el periodo (solo en modo predefinido)
  useEffect(() => {
    if (customMode) return;
    const [df, dt] = getActiveDates();
    fetchCausality(df, dt);
    fetchPublications(df, dt);
    // Changes is lazy-loaded; period buttons call fetchChanges directly when needed
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const [changes, setChanges] = useState(null);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [detectingChanges, setDetectingChanges] = useState(false);
  const [detectedChanges, setDetectedChanges] = useState(null);
  const [recentChanges, setRecentChanges] = useState(null);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const fetchChanges = async (dateFrom = null, dateTo = null) => {
    setLoadingChanges(true);
    try {
      let url = '/api/analytics/changes';
      if (dateFrom && dateTo) url += `?date_from=${dateFrom}&date_to=${dateTo}`;
      const r = await apiClient.get(url);
      setChanges(r.data);
    } catch (e) {
      toast.error('Error');
    } finally {
      setLoadingChanges(false);
    }
  };

  const dismissChange = async (itemId, eventType, eventDate) => {
    try {
      await apiClient.post('/api/analytics/dismiss-change', { item_id: itemId, event_type: eventType, event_date: eventDate });
      setChanges(prev => prev ? { ...prev, events: (prev.events || []).filter(e => !(e.item_id === itemId && e.event_type === eventType)) } : prev);
      toast.success('Cambio descartado');
    } catch { toast.error('Error al descartar'); }
  };

  const dismissActionable = async (itemId, actionableText) => {
    try {
      await apiClient.post('/api/analytics/dismiss-actionable', { item_id: itemId, actionable_text: actionableText });
      setPublications(prev => {
        if (!prev || !prev.publications) return prev;
        return { ...prev, publications: prev.publications.map(p => 
          p.item_id === itemId ? { ...p, accionables: (p.accionables || []).filter(a => a !== actionableText) } : p
        )};
      });
      toast.success('Oculto por 15 dias');
    } catch { toast.error('Error'); }
  };

  // Determina si un cambio detectado corresponde a un accionable específico
  const changeMatchesActionable = (changeType, actionableText) => {
    const t = actionableText.toUpperCase();
    if (changeType === 'portada') return t.includes('FOTO PORTADA') || t.includes('REVAMP TOTAL');
    if (changeType === 'galeria') return t.includes('CONTENIDO') || t.includes('REVAMP TOTAL');
    if (changeType === 'video') return t.includes('CONTENIDO') || t.includes('REVAMP TOTAL');
    if (changeType === 'titulo') return t.includes('REVAMP TOTAL');
    return false;
  };

  // Cargar cambios recientes (detectados automáticamente en las últimas 24h)
  const fetchRecentChanges = async () => {
    setLoadingRecent(true);
    try {
      const r = await apiClient.get('/api/analytics/recent-changes?hours=24');
      if (r.data.events_count > 0) {
        setRecentChanges(r.data);
      }
    } catch (e) {
      console.error('Error loading recent changes:', e);
    } finally {
      setLoadingRecent(false);
    }
  };

  const detectChangesNow = async () => {
    setDetectingChanges(true);
    setDetectedChanges(null);
    try {
      const r = await apiClient.post('/api/analytics/detect-changes');
      setDetectedChanges(r.data);
      if (r.data.events_count > 0) {
        toast.success(`${r.data.events_count} cambios detectados en ${r.data.changes?.length || 0} publicaciones`);
        const [df, dt] = getActiveDates();
        fetchChanges(df, dt);
        // Limpiar recent changes ya que ahora tenemos los detectados manualmente
        setRecentChanges(null);
      } else {
        toast.info('No se detectaron cambios nuevos');
      }
    } catch (e) {
      toast.error('Error al detectar cambios');
    } finally {
      setDetectingChanges(false);
    }
  };

  const downloadCSV = (rows, filename) => {
    if (!rows || rows.length === 0) { toast.error('No hay datos para descargar'); return; }
    const headers = Object.keys(rows[0]);
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV descargado');
  };

  const handleDownloadCausality = () => {
    if (!causality) return;
    const allItems = [
      ...(causality.drill_down_negative || []),
      ...(causality.drill_down_positive || []),
    ];
    const seen = new Set();
    const unique = allItems.filter(d => { if (seen.has(d.item_id)) return false; seen.add(d.item_id); return true; });
    unique.sort((a, b) => a.change - b.change);
    const rows = unique.map(d => ({
      Item_ID: d.item_id,
      Titulo: d.title,
      SKU: d.sku || '',
      Revenue_Actual: d.revenue_current,
      Revenue_Anterior: d.revenue_previous,
      Cambio_Ingresos: d.change,
      Ordenes_Actual: d.orders_current,
      Ordenes_Anterior: d.orders_previous,
      Unidades_Actual: d.units_current,
      Unidades_Anterior: d.units_previous,
      Visitas_Actual: d.visits_current,
      Visitas_Anterior: d.visits_previous,
      Visitas_Var_Pct: d.visits_pct,
      Conversion_Actual_Pct: d.conversion_current,
      Conversion_Anterior_Pct: d.conversion_previous,
      Conversion_Var_Pct: d.conversion_pct,
      Ticket_Actual: d.ticket_current,
      Ticket_Anterior: d.ticket_previous,
      Ticket_Var_Pct: d.ticket_pct,
      Impacto_Visitas: d.impact_visits,
      Impacto_Conversion: d.impact_conversion,
      Impacto_Ticket: d.impact_ticket,
      Causa_Principal: d.cause_tag,
    }));
    downloadCSV(rows, `analytics_causalidad_${period}d.csv`);
  };

  const handleDownloadPublications = () => {
    if (!publications?.publications) return;
    const rows = publications.publications.map(p => ({
      Item_ID: p.item_id,
      SKU: p.sku || '',
      Titulo: p.title,
      Pareto: p.pareto,
      Precio: p.price,
      Stock: p.stock,
      Revenue_30d: p.revenue_30d,
      Revenue_Pct: p.revenue_pct,
      Visitas_30d: p.visits_30d,
      Visitas_Organicas: p.organic_visits,
      Visitas_Ads: p.ads_visits,
      Ratio_Ads_Pct: p.ratio_ads,
      Ventas_30d: p.sales_30d,
      Unidades_30d: p.units_30d,
      Conversion_Pct: p.conversion_pct,
      CTR_Pct: p.ctr ?? '',
      Clicks_Ads: p.clicks ?? '',
      Impressions_Ads: p.impressions ?? '',
      Venta_Perdida: p.venta_perdida,
      Dias_Sin_Stock: p.dias_sin_stock,
      Variantes_Agotadas: p.variantes_agotadas,
      Total_Variantes: p.total_variantes,
      Accionables: (p.accionables || []).join(' | '),
    }));
    downloadCSV(rows, 'analytics_publicaciones.csv');
  };

  const handleDownloadChanges = () => {
    const events = changes?.events;
    if (!events || events.length === 0) { toast.error('No hay historial de cambios para descargar'); return; }
    const rows = events.map(ev => ({
      Item_ID: ev.item_id,
      Titulo: ev.title,
      Tipo_Cambio: ev.event_type,
      Label_Cambio: ev.event_label,
      Hace_Dias: ev.days_since,
      Estado: ev.status,
      Periodo_Antes: ev.period_before || '',
      Periodo_Despues: ev.period_after || '',
      Ordenes_Antes: ev.before?.orders ?? '',
      Ingresos_Antes: ev.before?.revenue ?? '',
      Visitas_Antes: ev.before?.visits ?? '',
      CVR_Antes_Pct: ev.before?.cvr ?? '',
      Ordenes_Despues: ev.after?.orders ?? '',
      Ingresos_Despues: ev.after?.revenue ?? '',
      Visitas_Despues: ev.after?.visits ?? '',
      CVR_Despues_Pct: ev.after?.cvr ?? '',
      CTR_Despues_Pct: ev.after?.ctr ?? '',
      Var_Ingresos_Pct: ev.deltas?.revenue_pct ?? '',
      Insight: ev.insight || '',
    }));
    downloadCSV(rows, 'analytics_cambios.csv');
  };

  // Cargar datos automáticamente al entrar a cada pestaña
  useEffect(() => {
    const [df, dt] = getActiveDates();
    if (tab === 'causality' && !causality && !loadingCausality) {
      fetchCausality(df, dt);
    }
    if (tab === 'publications' && !publications && !loadingPubs) {
      fetchPublications(df, dt);
    }
    if (tab === 'changes' && !changes && !recentChanges && !detectedChanges) {
      fetchRecentChanges();
      fetchChanges(df, dt);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Estado de sincronización
  const [syncMeta, setSyncMeta] = useState(null);
  const syncMetaLastFetch = useRef(0);
  const fetchSyncMeta = async () => {
    const now = Date.now();
    if (now - syncMetaLastFetch.current < 60_000) return;
    syncMetaLastFetch.current = now;
    try {
      const r = await apiClient.get('/api/sync/metadata');
      setSyncMeta(r.data);
    } catch (e) {}
  };

  const sortedPubs = publications?.publications?.slice().sort((a, b) => ((a[sortField] || 0) - (b[sortField] || 0)) * sortDir) || [];
  const filteredPubs = pubSearch.trim()
    ? sortedPubs.filter(p => {
        const q = pubSearch.trim().toLowerCase();
        return (p.title || '').toLowerCase().includes(q) || (p.item_id || '').toLowerCase().includes(q);
      })
    : sortedPubs;
  const handleSort = (f) => { if (sortField === f) setSortDir(d => d * -1); else { setSortField(f); setSortDir(-1); } };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-card/60 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
            <BarChart3 className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            <h1 className="text-2xl font-bold font-outfit text-foreground">Analytics</h1>
          </div>
          <div className="flex items-center gap-3">
            <TutorialVideoButton video="Analytics.mp4" caption="Cómo usar el módulo de Analytics." />
            {/* Estado de sincronización */}
            {syncMeta?.components && (() => {
              const visH = syncMeta.components.visitas?.hours_ago;
              const ordH = syncMeta.components.ordenes_profundo?.hours_ago;
              if (!Number.isFinite(visH) && !Number.isFinite(ordH)) return null;
              return (
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
                  {Number.isFinite(visH) && (
                    <span className={visH > 4 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/70'}>
                      Visitas: {visH.toFixed(1)}h
                    </span>
                  )}
                  {Number.isFinite(visH) && Number.isFinite(ordH) && (
                    <span className="text-slate-300 dark:text-slate-700">|</span>
                  )}
                  {Number.isFinite(ordH) && (
                    <span className={ordH > 12 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/70'}>
                      Órdenes: {ordH.toFixed(1)}h
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 py-6 max-w-7xl">
        <div className="flex gap-4 mb-6">
          <button onClick={() => setTab('causality')} className={'px-4 py-2 rounded-lg text-sm font-medium ' + (tab === 'causality' ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground')}>Análisis General</button>
          <button onClick={() => setTab('publications')} className={'px-4 py-2 rounded-lg text-sm font-medium ' + (tab === 'publications' ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground')}>Publicaciones</button>
          <button disabled title="Temporalmente no disponible" className="px-4 py-2 rounded-lg text-sm font-medium bg-muted text-muted-foreground opacity-50 cursor-not-allowed">Análisis de Cambios</button>
        </div>

        {tab === 'causality' && (
          <div>
            {/* Disclaimer de métricas brutas */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-amber-700 dark:text-amber-300">
                <strong>Métricas Brutas</strong> (igual que Mercado Libre). Incluye todas las órdenes generadas, sin descontar cancelaciones. 
                Para ver ingresos netos, usa el Dashboard principal.
              </span>
            </div>
            
            <div className="mb-6">
              <div className="flex gap-2 flex-wrap items-center">
                {[7, 15, 30].map(d => (
                  <button
                    key={d}
                    onClick={() => {
                      setPeriod(d); setCustomMode(false); setAppliedDateFrom(null); setAppliedDateTo(null);
                      const df = new Date(Date.now() - d * 86400000).toISOString().split('T')[0];
                      fetchCausality(df, today);
                      fetchPublications(df, today);
                      fetchChanges(df, today);
                    }}
                    disabled={loadingCausality}
                    className={'px-3 py-1.5 rounded-lg text-xs transition-colors ' + (!customMode && period === d ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground hover:bg-slate-700')}
                  >
                    {d} días
                  </button>
                ))}
                <button
                  onClick={() => setCustomMode(m => !m)}
                  disabled={loadingCausality}
                  className={'px-3 py-1.5 rounded-lg text-xs transition-colors ' + (customMode ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground hover:bg-slate-700')}
                >
                  Personalizado
                </button>

                <div className="flex items-center gap-2 ml-auto">
                  {causality && (
                    <button
                      onClick={handleDownloadCausality}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-muted hover:bg-slate-700 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      Descargar CSV
                    </button>
                  )}
                </div>
              </div>

              {/* Selector de rango personalizado */}
              {customMode && (
                <div className="mt-2 flex items-center gap-2 bg-muted/60 border border-border rounded-lg px-3 py-2 w-fit">
                  <input
                    type="date"
                    value={customDateFrom}
                    max={customDateTo}
                    onChange={e => setCustomDateFrom(e.target.value)}
                    disabled={loadingCausality}
                    className="bg-transparent text-foreground text-xs outline-none cursor-pointer disabled:opacity-60"
                  />
                  <span className="text-muted-foreground/70 text-xs">—</span>
                  <input
                    type="date"
                    value={customDateTo}
                    min={customDateFrom}
                    max={today}
                    onChange={e => setCustomDateTo(e.target.value)}
                    disabled={loadingCausality}
                    className="bg-transparent text-foreground text-xs outline-none cursor-pointer disabled:opacity-60"
                  />
                  <button
                    onClick={() => {
                      setAppliedDateFrom(customDateFrom);
                      setAppliedDateTo(customDateTo);
                      fetchCausality(customDateFrom, customDateTo);
                      fetchPublications(customDateFrom, customDateTo);
                      fetchChanges(customDateFrom, customDateTo);
                    }}
                    disabled={loadingCausality}
                    className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs font-medium px-3 py-1 rounded transition-colors"
                  >
                    Aplicar
                  </button>
                </div>
              )}

              {/* Etiqueta del periodo evaluado */}
              {(displayDateRange || causality?.period_current) && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  Periodo evaluado:{' '}
                  <span className="text-muted-foreground">
                    {causality?.period_current?.label ?? displayDateRange}
                  </span>
                  {causality?.period_previous && (
                    <span className="text-muted-foreground/70 ml-2">
                      vs <span className="text-muted-foreground">{causality.period_previous.label}</span>
                    </span>
                  )}
                </p>
              )}
            </div>
            {loadingCausality && !causality && (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin" />
              </div>
            )}
            {causality && (
              <div>
                {/* 1. Insight principal */}
                <div className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl p-6 mb-6">
                  <p className="text-lg text-foreground font-medium">{causality.insight}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-muted-foreground">
                      {causality.period_current && causality.period_previous
                        ? `${causality.period_current.label} vs ${causality.period_previous.label}`
                        : causality.period}
                    </p>
                    <button 
                      onClick={async () => {
                        try {
                          const res = await apiClient.get(`/api/analytics/orders-diagnostic?days=${period}`);
                          const data = res.data;
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `ordenes_${period}dias.json`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success('Archivo descargado');
                        } catch (e) { 
                          console.error(e); 
                          toast.error('Error al descargar: ' + (e.response?.data?.detail || e.message));
                        }
                      }}
                      className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 underline"
                    >
                      Descargar órdenes ({period}d)
                    </button>
                  </div>
                </div>
                
                {/* 2. Métricas de comparación */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Ingresos Brutos', curr: '$' + fmt(causality.current.revenue), prev: '$' + fmt(causality.previous.revenue), pct: causality.variations.revenue_pct, tooltip: 'Suma de todas las órdenes generadas' },
                    { label: 'Cantidad de Ventas', curr: fmt(causality.current.orders), prev: fmt(causality.previous.orders), pct: causality.variations.orders_pct, tooltip: 'Número total de órdenes' },
                    { label: 'Visitas', curr: fmt(causality.current.visits), prev: fmt(causality.previous.visits), pct: causality.variations.visits_pct },
                    { label: 'Conversión', curr: causality.current.conversion + '%', prev: causality.previous.conversion + '%', pct: causality.variations.conversion_pct, tooltip: 'Órdenes / Visitas' },
                  ].map((m, i) => (
                    <div key={i} className="bg-card/60 border border-border rounded-xl p-4 relative group">
                      <p className="text-xs text-muted-foreground mb-1">{m.label}</p>
                      <p className="text-xl font-bold text-foreground">{m.curr}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground/70">ant: {m.prev}</span>
                        {m.pct !== null && <ChangeTag value={m.pct} />}
                      </div>
                      {m.tooltip && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-muted text-xs text-muted-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          {m.tooltip}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* 3. Impacto en ventas por variable */}
                {causality.impacts && (
                  <div className="bg-card/60 border border-border rounded-xl p-4 mb-6">
                    <h3 className="text-sm font-semibold text-muted-foreground mb-4">Impacto en ventas por variable</h3>
                    <div className="space-y-3">
                      {causality.impacts.map((imp, i) => {
                        const maxImpact = Math.max(...causality.impacts.map(x => Math.abs(x.impact_money)));
                        const barWidth = Math.abs(imp.impact_money) / maxImpact * 100;
                        const isPositive = imp.impact_money >= 0;
                        return (
                          <div key={i} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-foreground">{imp.factor}</span>
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-bold ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                  {isPositive ? '+' : ''}{fmtMoney(imp.impact_money)}
                                </span>
                                <span className="text-xs text-muted-foreground/70 w-16 text-right">
                                  ({imp.variation_pct >= 0 ? '+' : ''}{imp.variation_pct}%)
                                </span>
                              </div>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${isPositive ? 'bg-green-500' : 'bg-red-500'}`}
                                style={{ width: `${barWidth}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* 4. Drill-down por publicación */}
                <div className="grid grid-cols-2 gap-4">
                  {[['drill_down_negative', 'Mayor caída', 'red'], ['drill_down_positive', 'Mayor crecimiento', 'green']].map(([key, title, color]) => (
                    <div key={key} className="bg-card/60 border border-border rounded-xl p-4">
                      <h3 className={'text-sm font-semibold mb-3 ' + (color === 'green' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400')}>{title}</h3>
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                        {(causality[key] || []).map((d, i) => (
                          <div key={i} className="bg-muted/30 rounded-lg p-3 hover:bg-muted transition-colors">
                            <div className="flex items-start gap-3">
                              {d.thumbnail && <img src={d.thumbnail} alt="" className="w-10 h-10 rounded" />}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className="min-w-0">
                                    <p className="text-xs text-foreground font-medium truncate max-w-[150px]">{d.title}</p>
                                    <span className="text-[9px] text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded font-mono">{d.item_id}</span>
                                  </div>
                                  <span className={`text-sm font-bold ${color === 'green' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                                    {color === 'green' ? '+' : ''}{fmtMoney(d.change)}
                                  </span>
                                </div>
                                {/* Etiqueta de causa raíz */}
                                <p className="text-[10px] text-muted-foreground mt-1">{d.cause_tag}</p>
                                {/* Mini métricas */}
                                <div className="flex items-center gap-3 mt-2 text-[10px]">
                                  <span className="text-muted-foreground/70">
                                    Visitas: <ChangeTag value={d.visits_pct} />
                                  </span>
                                  <span className="text-muted-foreground/70">
                                    Conv: <ChangeTag value={d.conversion_pct} />
                                  </span>
                                </div>
                              </div>
                              {/* Botón analizar */}
                              <button 
                                onClick={() => setSelectedProduct(d)}
                                className="text-muted-foreground/70 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors p-1"
                                title="Analizar"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Modal de diagnóstico */}
                {selectedProduct && (
                  <ProductDiagnosticModal 
                    product={selectedProduct} 
                    onClose={() => setSelectedProduct(null)} 
                  />
                )}
              </div>
            )}
            {!causality && !loadingCausality && (<div className="text-center py-16"><BarChart3 className="w-12 h-12 text-muted-foreground/70 mx-auto mb-3" /><p className="text-muted-foreground">Selecciona un periodo y presiona Analizar</p></div>)}
          </div>
        )}

        {tab === 'publications' && (
          <div>
            {!publications && !loadingPubs && (<div className="text-center py-16"><Button onClick={() => { const [df, dt] = getActiveDates(); fetchPublications(df, dt); }} className="bg-cyan-600 hover:bg-cyan-500">Cargar Publicaciones</Button></div>)}
            {loadingPubs && <div className="text-center py-16"><RefreshCw className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin mx-auto" /></div>}
            {publications && (
              <div>
                {/* Header con info de caché y botón refresh */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {publications.period_label && (
                      <span className="text-xs text-muted-foreground">
                        Periodo: <span className="text-muted-foreground">{publications.period_label}</span>
                      </span>
                    )}
                    {publications.from_cache && Number.isFinite(publications.cache_age_minutes) && (
                      <span className="text-[10px] text-muted-foreground/70 bg-muted px-2 py-1 rounded">
                        Caché: {publications.cache_age_minutes < 1 ? '<1' : Math.round(publications.cache_age_minutes)} min
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-muted-foreground/70 absolute left-2 top-1/2 -translate-y-1/2" />
                      <Input
                        type="text"
                        value={pubSearch}
                        onChange={(e) => setPubSearch(e.target.value)}
                        placeholder="Buscar por nombre o ID..."
                        data-testid="publications-search-input"
                        className="h-7 w-56 pl-7 text-xs"
                      />
                    </div>
                    <button
                      onClick={handleDownloadPublications}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-muted hover:bg-slate-700 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                      title="Descargar CSV con todas las publicaciones"
                    >
                      <Download className="w-3 h-3" />
                      Descargar CSV
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{publications.pareto_summary?.killers || 0}</p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">KILLER (80% ingresos)</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{publications.pareto_summary?.potencial || 0}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">POTENCIAL (15%)</p>
                  </div>
                  <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{publications.pareto_summary?.cola_larga || 0}</p>
                    <p className="text-xs text-muted-foreground">COLA LARGA (5%)</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">${fmt(publications.total_venta_perdida)}</p>
                    <p className="text-xs text-red-700 dark:text-red-300">Venta Perdida por Stock</p>
                  </div>
                </div>
                <div className="bg-card/60 border border-border rounded-xl overflow-hidden">
                  <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0 z-10">
                        <tr className="text-muted-foreground text-xs">
                          <th className="px-2 py-3 text-left">Pareto</th>
                          <th className="px-2 py-3 text-left">Publicacion</th>
                          {[['revenue_30d','Ingresos'],['revenue_pct','%'],['visits_30d','Visitas'],['ratio_ads','Trafico'],['sales_30d','Ventas'],['conversion_pct','CVR'],['ctr','CTR'],['stock','Stock'],['venta_perdida','Vta Perdida']].map(([f,l]) => (
                            <th key={f} className="px-2 py-3 text-center cursor-pointer hover:text-foreground" onClick={() => handleSort(f)}>{l} {sortField===f?(sortDir===-1?'↓':'↑'):''}</th>
                          ))}
                          <th className="px-2 py-3 text-left">Accionables</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredPubs.length === 0 && (
                          <tr><td colSpan={12} className="px-2 py-8 text-center text-muted-foreground text-xs">No se encontraron publicaciones que coincidan con "{pubSearch}"</td></tr>
                        )}
                        {filteredPubs.map(p => (
                          <tr key={p.item_id} className="hover:bg-muted/20">
                            <td className="px-2 py-2"><span className={'text-[10px] px-1.5 py-0.5 rounded-full font-bold ' + (p.pareto==='KILLER'?'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400':p.pareto==='POTENCIAL'?'bg-blue-500/20 text-blue-600 dark:text-blue-400':'bg-slate-500/20 text-muted-foreground/70')}>{p.pareto==='KILLER'?'KILLER':p.pareto==='POTENCIAL'?'POTENCIAL':'COLA'}</span></td>
                            <td className="px-2 py-2"><div className="flex items-center gap-2">{p.thumbnail && <img src={p.thumbnail} alt="" className="w-7 h-7 rounded" />}<div><p className="text-foreground text-xs truncate max-w-[130px]">{p.title}</p><p className="text-[9px] text-muted-foreground/70">{p.item_id}</p></div></div></td>
                            <td className="px-2 py-2 text-center text-green-600 dark:text-green-400 font-medium">${fmt(p.revenue_30d)}</td>
                            <td className="px-2 py-2 text-center text-muted-foreground">{p.revenue_pct}%</td>
                            <td className="px-2 py-2 text-center text-muted-foreground">{fmt(p.visits_30d)}</td>
                            <td className="px-2 py-2 text-center">{p.ratio_ads > 0 ? <span className={p.traffic_color==='red'?'text-red-600 dark:text-red-400 text-xs':p.traffic_color==='green'?'text-green-600 dark:text-green-400 text-xs':'text-yellow-600 dark:text-yellow-400 text-xs'}>{p.ratio_ads}%Ads</span> : <span className="text-muted-foreground/70">-</span>}</td>
                            <td className="px-2 py-2 text-center text-foreground font-medium">{p.sales_30d}</td>
                            <td className="px-2 py-2 text-center"><span className={p.conversion_pct>=3?'text-green-600 dark:text-green-400':p.conversion_pct>0?'text-red-600 dark:text-red-400':'text-muted-foreground/70'}>{p.conversion_pct>0?p.conversion_pct+'%':'-'}</span></td>
                            <td className="px-2 py-2 text-center">{p.ctr!=null?<span className={p.ctr>=0.4?'text-green-600 dark:text-green-400':'text-red-600 dark:text-red-400'}>{p.ctr}%</span>:<span className="text-muted-foreground/70">-</span>}</td>
                            <td className="px-2 py-2 text-center text-blue-600 dark:text-blue-400">{p.stock}</td>
                            <td className="px-2 py-2 text-center">{p.venta_perdida>0?<span className="text-red-600 dark:text-red-400">${fmt(p.venta_perdida)}</span>:<span className="text-muted-foreground/70">-</span>}</td>
                            <td className="px-2 py-2">
                              <div className="flex items-start gap-2">
                                <div className="flex-1 space-y-1">
                                  {p.accionables?.length > 0 && p.accionables.map((a, i) => (
                                    <div key={i} className="flex items-center gap-1">
                                      <p className="text-[9px] text-amber-600 dark:text-amber-400 leading-tight flex-1">{a}</p>
                                      {p.change_info && changeMatchesActionable(p.change_info.type, a) && (
                                        <button
                                          data-testid={`dismiss-actionable-${p.item_id}-${i}`}
                                          onClick={() => dismissActionable(p.item_id, a)}
                                          className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors shrink-0 p-0.5 rounded hover:bg-red-500/10"
                                          title="Ya lo hice, ocultar 15 dias"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {p.change_info && (
                                  <div className="shrink-0 bg-cyan-500/10 border border-cyan-500/30 rounded px-1.5 py-1">
                                    <p className="text-[8px] text-cyan-700 dark:text-cyan-300 whitespace-nowrap">
                                      {p.change_info.type === 'titulo' ? 'Titulo' : p.change_info.type === 'portada' ? 'Portada' : p.change_info.type === 'galeria' ? 'Imgs' : 'Video'} {p.change_info.date}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'changes' && (
          <div>
            {/* Botón Detectar Cambios */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Análisis de Cambios</h3>
                <p className="text-xs text-muted-foreground">Detecta modificaciones en tus publicaciones (título, precio, fotos, video, logística)</p>
              </div>
              <Button 
                onClick={detectChangesNow} 
                disabled={detectingChanges}
                className="bg-cyan-600 hover:bg-cyan-500"
                data-testid="detect-changes-btn"
              >
                {detectingChanges ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Escaneando publicaciones...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Detectar Cambios Ahora
                  </>
                )}
              </Button>
            </div>
            
            {/* Loading de cambios recientes */}
            {loadingRecent && (
              <div className="bg-muted/30 border border-border rounded-xl p-4 mb-6 flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-cyan-600 dark:text-cyan-400 animate-spin" />
                <span className="text-muted-foreground text-sm">Cargando cambios recientes...</span>
              </div>
            )}
            
            {/* Cambios detectados AUTOMÁTICAMENTE (últimas 24h) */}
            {recentChanges && recentChanges.changes?.length > 0 && !detectedChanges && (
              <div className="bg-gradient-to-r from-amber-600/10 to-orange-600/10 border border-amber-500/30 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                  <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                    Cambios Detectados Automáticamente (últimas 24h)
                  </h4>
                  <span className="text-xs text-amber-600/60 dark:text-amber-400/60 ml-auto">{recentChanges.events_count} cambios en {recentChanges.changes.length} publicaciones</span>
                </div>
                <div className="space-y-3">
                  {recentChanges.changes.map((item, idx) => (
                    <div key={idx} className="bg-card/60 border border-border rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        {item.thumbnail && <img src={item.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-foreground font-medium text-sm truncate">{item.title}</p>
                            <span className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">{item.item_id}</span>
                          </div>
                          <div className="space-y-1.5 mt-2">
                            {item.changes.map((change, cIdx) => (
                              <div key={cIdx} className="flex items-center gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                  change.tipo === 'Precio' ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300' :
                                  change.tipo === 'Título' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' :
                                  change.tipo === 'Foto Portada' ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' :
                                  change.tipo === 'Video' ? 'bg-pink-500/20 text-pink-700 dark:text-pink-300' :
                                  change.tipo === 'Imágenes Publicación' ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' :
                                  change.tipo === 'Logística' ? 'bg-green-500/20 text-green-700 dark:text-green-300' :
                                  'bg-slate-500/20 text-muted-foreground'
                                }`}>
                                  {change.tipo}
                                </span>
                                <span className="text-muted-foreground/70 line-through truncate max-w-[120px]">{change.antes}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-foreground font-medium truncate max-w-[150px]">{change.ahora}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Resultados de detección MANUAL (botón) */}
            {detectedChanges && detectedChanges.changes?.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-600/10 to-teal-600/10 border border-emerald-500/30 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    Cambios Detectados ({detectedChanges.events_count} cambios en {detectedChanges.changes.length} publicaciones)
                  </h4>
                </div>
                <div className="space-y-3">
                  {detectedChanges.changes.map((item, idx) => (
                    <div key={idx} className="bg-card/60 border border-border rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        {item.thumbnail && <img src={item.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-foreground font-medium text-sm truncate">{item.title}</p>
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">{item.item_id}</span>
                          </div>
                          <div className="space-y-1.5 mt-2">
                            {item.changes.map((change, cIdx) => (
                              <div key={cIdx} className="flex items-center gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded-full font-medium ${
                                  change.tipo === 'Precio' ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300' :
                                  change.tipo === 'Título' ? 'bg-blue-500/20 text-blue-700 dark:text-blue-300' :
                                  change.tipo === 'Foto Portada' ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' :
                                  change.tipo === 'Video' ? 'bg-pink-500/20 text-pink-700 dark:text-pink-300' :
                                  change.tipo === 'Imágenes Publicación' ? 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300' :
                                  change.tipo === 'Logística' ? 'bg-green-500/20 text-green-700 dark:text-green-300' :
                                  'bg-slate-500/20 text-muted-foreground'
                                }`}>
                                  {change.tipo}
                                </span>
                                <span className="text-muted-foreground/70 line-through truncate max-w-[120px]">{change.antes}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-foreground font-medium truncate max-w-[150px]">{change.ahora}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Mensaje si no hay cambios detectados */}
            {detectedChanges && detectedChanges.events_count === 0 && (
              <div className="bg-muted/30 border border-border rounded-xl p-6 mb-6 text-center">
                <p className="text-muted-foreground">No se detectaron cambios nuevos.</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Se revisaron {detectedChanges.items_checked} publicaciones</p>
              </div>
            )}
            
            {loadingChanges && <div className="text-center py-16"><RefreshCw className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin mx-auto" /></div>}
            
            {/* Historial de cambios */}
            {changes && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-muted-foreground">Historial de Cambios</h4>
                  <button
                    onClick={handleDownloadChanges}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-muted hover:bg-slate-700 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                    title="Descargar CSV del historial de cambios"
                  >
                    <Download className="w-3 h-3" />
                    Descargar CSV
                  </button>
                </div>
                {!changes.has_real_data && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-700 dark:text-amber-300">Datos de ejemplo. Los cambios reales se detectaran automaticamente a partir de hoy.</p>
                  </div>
                )}
                <div className="space-y-4">
                  {changes.events?.map((ev, i) => (
                    <div key={i} className={'border rounded-2xl p-5 ' + (ev.status === 'positive' ? 'border-green-500/30 bg-green-500/5' : ev.status === 'negative' ? 'border-red-500/30 bg-red-500/5' : 'border-border bg-card/60')}>
                      <div className="flex items-start gap-4">
                        {ev.thumbnail && <img src={ev.thumbnail} alt="" className="w-12 h-12 rounded-lg" />}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={'text-xs px-2 py-0.5 rounded-full font-medium ' + (
                              ev.event_type === 'portada' ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' : 
                              ev.event_type === 'video' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' : 
                              ev.event_type === 'galeria' ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' : 
                              ev.event_type === 'precio' ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400' :
                              ev.event_type === 'titulo' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' :
                              ev.event_type === 'logistica' ? 'bg-teal-500/20 text-teal-600 dark:text-teal-400' :
                              'bg-green-500/20 text-green-600 dark:text-green-400'
                            )}>{ev.event_label}</span>
                            <span className="text-xs text-muted-foreground/70">hace {ev.days_since} dias</span>
                            <span className={'text-sm font-bold ml-auto ' + (ev.deltas?.revenue_pct > 0 ? 'text-green-600 dark:text-green-400' : ev.deltas?.revenue_pct < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                              {ev.deltas?.revenue_pct > 0 ? '+' : ''}{ev.deltas?.revenue_pct || 0}%
                            </span>
                            <button
                              data-testid={`dismiss-change-${ev.item_id}`}
                              onClick={() => dismissChange(ev.item_id, ev.event_type, ev.event_date)}
                              className="ml-2 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-500/10"
                              title="Descartar este cambio"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-foreground text-sm font-medium">{ev.title}</p>
                            <span className="text-[10px] text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded">{ev.item_id}</span>
                          </div>
                          <p className="text-muted-foreground text-xs mt-1 mb-3">{ev.insight}</p>

                          {/* Tabla comparativa */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-muted rounded-lg p-3">
                              <p className="text-[10px] text-muted-foreground/70 mb-2">ANTES <span className="text-muted-foreground/70">{ev.period_before}</span></p>
                              <div className="grid grid-cols-4 gap-2 text-center">
                                <div><p className="text-[9px] text-muted-foreground/70">Ventas</p><p className="text-xs text-muted-foreground">{ev.before?.orders || 0}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">Ingresos</p><p className="text-xs text-muted-foreground">${fmt(ev.before?.revenue || 0)}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">Visitas</p><p className="text-xs text-muted-foreground">{fmt(ev.before?.visits || 0)}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">CVR</p><p className="text-xs text-muted-foreground">{ev.before?.cvr || 0}%</p></div>
                              </div>
                            </div>
                            <div className={'rounded-lg p-3 ' + (ev.status === 'positive' ? 'bg-green-500/10' : ev.status === 'negative' ? 'bg-red-500/10' : 'bg-muted')}>
                              <p className="text-[10px] text-muted-foreground/70 mb-2">DESPUES <span className="text-muted-foreground/70">{ev.period_after}</span></p>
                              <div className="grid grid-cols-5 gap-2 text-center">
                                <div><p className="text-[9px] text-muted-foreground/70">Ventas</p><p className="text-xs text-foreground font-medium">{ev.after?.orders || 0}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">Ingresos</p><p className="text-xs text-foreground font-medium">${fmt(ev.after?.revenue || 0)}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">Visitas</p><p className="text-xs text-foreground font-medium">{fmt(ev.after?.visits || 0)}</p></div>
                                <div><p className="text-[9px] text-muted-foreground/70">CVR</p><p className="text-xs text-foreground font-medium">{ev.after?.cvr || 0}%</p></div>
                                {ev.after?.ctr != null && <div><p className="text-[9px] text-muted-foreground/70">CTR</p><p className="text-xs text-foreground font-medium">{ev.after.ctr}%</p></div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!changes && !loadingChanges && (
              <div className="text-center py-16">
                <Button onClick={() => { const [df, dt] = getActiveDates(); fetchChanges(df, dt); }} className="bg-cyan-600 hover:bg-cyan-500">Cargar Historial de Cambios</Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Analytics;
